export interface StatusGroup {
  name: string;
  color: string;
  bg: string;
  border: string;
  isReturn: boolean;
  isKontant?: boolean;
  isDirekte?: boolean;
  isPlukk?: boolean;
  isBkCode?: boolean;
  isReturnToSupplier?: boolean;
    statuses: string[] | null; // null = special logic (Alle ordre / Kredit / Kontant / Direkte ordre / Plukkordre / Ordre med BK kode / Retur til leverandør)
}

/** orderPaymentMethod values that count as "Kontant". */
export const KONTANT_PAYMENT_METHODS = ['7', '07', '49'];

/** directOrderFlag (O-DIRREG) values that count as "Direkte ordre". */
export const DIREKTE_ORDER_TYPES = ['J'];

/** An order counts as "Plukkordre" when O-SKAFF='P'. */
export const isPlukkOrdre = (skaff: string) => skaff === 'P';

/** An order counts as "Retur til leverandør" when O-BK='B' — this is a return to the supplier,
 *  not a sale, so it gets its own box and is excluded from the sales totals (Alle ordre and
 *  Ordre med BK kode) instead of being lumped in with other BK codes. */
export const isReturnToSupplierBk = (bk: string) => bk.trim() === 'B';

/** An order counts as "Ordre med BK kode" when O-BK is set to any code (non-blank) — except
 *  'K', which every kontantordre carries and would otherwise make this box redundant with
 *  "Kontant" instead of highlighting orders with a genuinely notable BK code, and 'B' (retur til
 *  leverandør), which has its own box and must not inflate this box's sum either. */
export const hasBkCode = (bk: string) => bk.trim() !== '' && bk.trim() !== 'K' && !isReturnToSupplierBk(bk);

export const STATUS_GROUPS: StatusGroup[] = [
  {
    name: 'Alle ordre',
    color: '#1976d2',
    bg: '#e3f2fd',
    border: '#1976d2',
    isReturn: false,
    statuses: null,
  },
  {
    name: 'Ferdigmeldt',
    color: '#2e7d32',
    bg: '#e8f5e9',
    border: '#2e7d32',
    isReturn: false,
    // 'Tilbudsordre'/'Utlevert'/'Mottatt i VDA' (and their K-status ALL-CAPS counterparts
    // 'UTLEVERT.'/'MOTTATT I VDA.') are extra outcomes of status F/J/K — see OrderSummaryMapper.
    statuses: ['Ferdigmeldt', 'Fmeld./KS-oppgjør', 'Lagerovf. (f.meldt)', 'Verkstedsord. (f.meldt)', 'Fakturert', 'Forhånds ordre', 'Tilbudsordre', 'Påbegynt samleordre', 'Utlevert', 'UTLEVERT.', 'Mottatt i VDA', 'MOTTATT I VDA.'],
  },
  {
    name: 'Venteordre',
    color: '#f9a825',
    bg: '#fffde7',
    border: '#f9a825',
    isReturn: false,
    statuses: ['Venter godkjenning', 'Sluppet forhåndsordre', 'OVERFØRT VDA'],
  },
  {
    name: 'Printet',
    color: '#7b1fa2',
    bg: '#f3e5f5',
    border: '#7b1fa2',
    isReturn: false,
    // 'Vente / plukkordre VDA'/'Venteordre/plukkordre' are the Skaff=='P' outcomes of status J
    // (see OrderSummaryMapper.MapStartedOrderStatus) — grouped here with the other
    // picking/packing-adjacent statuses rather than "Venteordre", per testing team's preference
    // to avoid a box that reads as a duplicate of the "Plukkordre" cross-filter. These orders
    // still also count toward "Plukkordre" (Skaff=='P') — that overlap is expected, same as any
    // other primary-box/cross-filter overlap (e.g. "Ferdigmeldt" + "Kontant").
    // 'PRTET PÅ {id} FRA VDA' embeds a dynamic printer id and is matched by prefix in
    // statusInGroup below instead of listed here.
    statuses: ['Printet', 'Pakket', 'Ferdig plukket', 'PRINTET. FRA VDA', 'MAIL ER SENDT', 'Vente / plukkordre VDA', 'Venteordre/plukkordre'],
  },
  {
    name: 'Registrert',
    color: '#1976d2',
    bg: '#e3f2fd',
    border: '#1976d2',
    isReturn: false,
    statuses: ['Ferdig registrert'],
  },
  {
    name: 'Ikke fullført',
    color: '#d32f2f',
    bg: '#fdecea',
    border: '#d32f2f',
    isReturn: false,
    statuses: ['Påbegynt, ikke fullført', 'Kred-stopp, ikke print', 'Ufullstendig'],
  },
  {
    name: 'Utgårmeldt',
    color: '#757575',
    bg: '#f5f5f5',
    border: '#9e9e9e',
    isReturn: false,
    statuses: ['Utgårmeldt', 'Korr. etter f.melding', 'Restreg. (f.meldt)', 'Retur til lev.', 'Delferdigmeldt'],
  },
  {
    name: 'Kontant',
    color: '#00695c',
    bg: '#e0f2f1',
    border: '#00695c',
    isReturn: false,
    isKontant: true,
    statuses: null,
  },
  {
    name: 'Direkte ordre',
    color: '#e65100',
    bg: '#fff3e0',
    border: '#e65100',
    isReturn: false,
    isDirekte: true,
    statuses: null,
  },
  {
    name: 'Plukkordre',
    // Was '#6a1b9a'/'#f3e5f5' — same purple family and the exact same background as "Printet",
    // making the two cards hard to tell apart. Yellow instead, matching "Venteordre"'s palette,
    // since most Plukkordre orders are the Skaff=='P' "venteordre" outcomes of status J.
    color: '#f9a825',
    bg: '#fffde7',
    border: '#f9a825',
    isReturn: false,
    isPlukk: true,
    statuses: null,
  },
  {
      name: 'Ordre med BK kode',
    color: '#ad1457',
    bg: '#fce4ec',
    border: '#ad1457',
    isReturn: false,
    isBkCode: true,
    statuses: null,
  },
  {
    name: 'Kredit',
    color: '#d32f2f',
    bg: '#fff5f5',
    border: '#d32f2f',
    isReturn: true,
    statuses: null,
  },
  {
    name: 'Retur til leverandør',
    color: '#6d4c41',
    bg: '#efebe9',
    border: '#6d4c41',
    isReturn: false,
    isReturnToSupplier: true,
    statuses: null,
  },
];

/** All backend status strings that belong to a given group name. */
export function getGroupStatuses(groupName: string): string[] | null {
  return STATUS_GROUPS.find(g => g.name === groupName)?.statuses ?? null;
}

/** Whether a backend status string belongs to the given group. */
export function statusInGroup(group: StatusGroup, status: string): boolean {
  if (!group.statuses) return false;
  if (group.statuses.includes(status)) return true;
  // MapPrintedOrderStatus embeds the printer id in "PRTET PÅ {id} FRA VDA", so that one can't be
  // listed literally — bucket by prefix instead.
  if (group.name === 'Printet') {
    return status.startsWith('PRTET PÅ');
  }
  return false;
}
